import { Component, Director, director, Layout, Node, _decorator } from 'cc';
import { DEV } from 'cc/env';
import FlexItem from './FlexItem';

const { ccclass, property, executeInEditMode, requireComponent } = _decorator;

@ccclass('FlexContainer')
@executeInEditMode
@requireComponent(Layout)
export default class FlexContainer extends Component {
    @property
    private _paddingLeft: number = 0;

    @property
    private _paddingRight: number = 0;

    @property
    private _spacingX: number = 0;

    private _layout: Layout = null;
    private readonly _flexItemMap: Map<Node, FlexItem> = new Map();
    private _dirty: boolean = false;

    @property({ tooltip: DEV && 'The padding on the left side of the flex container.' })
    public get paddingLeft(): number {
        return this._paddingLeft;
    }
    public set paddingLeft(value: number) {
        if (this._paddingLeft === value) {
            return;
        }
        this._paddingLeft = value;
        this.layout.paddingLeft = value;
        this._markDirty();
    }

    @property({ tooltip: DEV && 'The padding on the right side of the flex container.' })
    public get paddingRight(): number {
        return this._paddingRight;
    }
    public set paddingRight(value: number) {
        if (this._paddingRight === value) {
            return;
        }
        this._paddingRight = value;
        this.layout.paddingRight = value;
        this._markDirty();
    }

    @property({ tooltip: DEV && 'The spacing between flex items in x axis.' })
    public get spacingX(): number {
        return this._spacingX;
    }
    public set spacingX(value: number) {
        if (this._spacingX === value) {
            return;
        }
        this._spacingX = value;
        this.layout.spacingX = value;
        this._markDirty();
    }

    private get layout(): Layout {
        if (!this._layout) {
            this._layout = this.getComponent(Layout);
        }
        return this._layout;
    }

    public onEnable() {
        director.on(Director.EVENT_AFTER_UPDATE, this.updateLayout, this);
        this._registerEventListener();
        this._markDirty();
    }

    public onDisable() {
        director.off(Director.EVENT_AFTER_UPDATE, this.updateLayout, this);
        this._unregisterEventListener();
    }

    public updateLayout(force = false) {
        if (!this._dirty && !force) {
            return;
        }
        this._dirty = false;
        let availableWidth = this.node.transformWidth;
        const nonFlexItems: Node[] = this.node.children.filter((child) => !this._flexItemMap.has(child));
        const nonFlexItemTotalWidth = nonFlexItems.reduce((sum, child) => sum + child.transformWidth, 0);
        const flexItems: FlexItem[] = this.node.children.filter((child) => this._flexItemMap.has(child)).map((child) => this._flexItemMap.get(child));
        const flexItemTotalWidth = flexItems.reduce((sum, child) => sum + child.node.transformWidth, 0);
        if (availableWidth <= flexItemTotalWidth) {
            flexItems.forEach((child) => {
                child.node.transformWidth = 0;
            });
            return;
        }
        availableWidth -= nonFlexItemTotalWidth + (this.node.children.length - 1) * this.spacingX + this.paddingLeft + this.paddingRight;
        const totalFlexFactor = flexItems.reduce((sum, child) => sum + child.flexFactor, 0);
        flexItems.forEach((flexItem) => {
            const percentage = flexItem.flexFactor / totalFlexFactor;
            flexItem.node.transformWidth = availableWidth * percentage;
        });
    }

    private _registerEventListener() {
        this.node.on(Node.EventType.CHILD_ADDED, this._onChildAdded, this);
        this.node.on(Node.EventType.CHILD_REMOVED, this._onChildRemoved, this);
        this.node.children.forEach((child) => this._onChildAdded(child));
    }

    private _unregisterEventListener() {
        this.node.off(Node.EventType.CHILD_ADDED, this._onChildAdded, this);
        this.node.off(Node.EventType.CHILD_REMOVED, this._onChildRemoved, this);
        this.node.children.forEach((child) => this._onChildRemoved(child));
    }

    private _onChildAdded(child: Node) {
        this._registerChildEventListener(child);
        const flexItem = child.getComponent(FlexItem);
        if (flexItem) {
            this._addFlexItem(flexItem);
        } else {
            child.on(FlexItem.EVENT_TYPE.ATTACH, this._addFlexItem, this);
        }
        this._markDirty();
    }

    private _onChildRemoved(child: Node) {
        this._unregisterChildEventListener(child);
        const flexItem = this._flexItemMap.get(child);
        if (flexItem) {
            this._removeFlexItem(flexItem);
        } else {
            child.off(FlexItem.EVENT_TYPE.ATTACH, this._addFlexItem, this);
        }
        this._markDirty();
    }

    private _registerChildEventListener(child: Node) {
        child.on(FlexItem.EVENT_TYPE.FLEX_FACTOR_CHANGED, this._markDirty, this);
        child.on(Node.EventType.SIZE_CHANGED, this._markDirty, this);
        child.on(Node.EventType.TRANSFORM_CHANGED, this._markDirty, this);
    }

    private _unregisterChildEventListener(child: Node) {
        child.off(FlexItem.EVENT_TYPE.FLEX_FACTOR_CHANGED, this._markDirty, this);
        child.off(Node.EventType.SIZE_CHANGED, this._markDirty, this);
        child.off(Node.EventType.TRANSFORM_CHANGED, this._markDirty, this);
    }

    private _addFlexItem(flexItem: FlexItem) {
        this._flexItemMap.set(flexItem.node, flexItem);
        flexItem.node.on(FlexItem.EVENT_TYPE.DETACH, this._removeFlexItem, this);
        this._markDirty();
    }

    private _removeFlexItem(flexItem: FlexItem) {
        this._flexItemMap.delete(flexItem.node);
        flexItem.node.off(FlexItem.EVENT_TYPE.DETACH, this._removeFlexItem, this);
        if (flexItem.node.parent === this.node) {
            flexItem.node.on(FlexItem.EVENT_TYPE.ATTACH, this._addFlexItem, this);
        }
        this._markDirty();
    }

    private _markDirty() {
        this._dirty = true;
    }
}
